import { computed, Injectable, signal } from "@angular/core";
import { TeamLeaderStateModel } from "../Dto/TeamLeaderStateModel";
import { TeamLeaderListItem } from "../Dto/TeamLeaderModel";
import { PaginationInfo } from "../Dto/TeamLeaderSearchResponse";
import { SearchTeamLeaderRequest } from "../Dto/TeamLeaderSearchRequest";

const initialState: TeamLeaderStateModel = {
  data: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  },
  searchParams: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

@Injectable({ providedIn: "root" })
export class TeamLeaderState {
  private readonly state = signal<TeamLeaderStateModel>(initialState);
  public readonly data = computed(() => this.state().data);
  public readonly pagination = computed(() => this.state().pagination);
  public readonly searchParams = computed(() => this.state().searchParams);
  public readonly loading = computed(() => this.state().loading);
  public readonly error = computed(() => this.state().error);

  setTeamLeaders(data: TeamLeaderListItem[], pagination: PaginationInfo): void {
    this.state.update((state) => ({
      ...state,
      data,
      pagination,
      loading: false,
      error: null,
    }));
  }

  setSearchParams(params: SearchTeamLeaderRequest): void {
    this.state.update((state) => ({
      ...state,
      searchParams: params,
    }));
  }

  setLoading(loading: boolean): void {
    this.state.update((state) => ({ ...state, loading }));
  }

  setError(error: string | null): void {
    this.state.update((state) => ({ ...state, error }));
  }

  updateTeamLeaderInState(id: string, data: Partial<TeamLeaderListItem>): void {
    this.state.update((state) => ({
      ...state,
      data: state.data.map((teamLeader) =>
        (() => {
          if (teamLeader.id === id) {
            return { ...teamLeader, ...data };
          }
          return teamLeader;
        })(),
      ),
      loading: false,
      error: null,
    }));
  }

  addTeamLeaderToState(teamLeader: TeamLeaderListItem): void {
    this.state.update((state) => ({
      ...state,
      data: [teamLeader, ...state.data],
      pagination: {
        ...state.pagination,
        total: state.pagination.total + 1,
      },
      loading: false,
      error: null,
    }));
  }

  removeTeamLeaderFromState(id: string): void {
    this.state.update((state) => ({
      ...state,
      data: state.data.filter((teamLeader) => teamLeader.id !== id),
      pagination: {
        ...state.pagination,
        total: Math.max(0, state.pagination.total - 1),
      },
      loading: false,
      error: null,
    }));
  }

  teamLeaderById = computed(() => {
    const teamLeaders = this.state().data;
    return (id: string) => teamLeaders.find((teamLeader) => teamLeader.id === id) || null;
  });
}
